import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Alert,
  AlertTitle,
  Chip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { SubmissionStatusFieldsFragment } from '@libs/graphql-types';

const useStyles = makeStyles((theme: Theme) => ({
  statusContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(4),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {},
  errorDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    '& pre': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      margin: 0,
    },
  },
  apiResponse: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    '& pre': {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
      margin: 0,
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadingProgressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
  },
}));

interface ApplicationStatusDisplayProps {
  data?: SubmissionStatusFieldsFragment | null;
  loading?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'SUBMITTED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'PROCESSING':
      return 'info';
    case 'FAILED':
    case 'ERROR':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'SUBMITTED':
      return i18n.t('applicationForm.status.submitted', { ns: 'jobForm' });
    case 'PENDING':
      return i18n.t('applicationForm.status.pending', { ns: 'jobForm' });
    case 'PROCESSING':
      return i18n.t('applicationForm.status.processing', { ns: 'jobForm' });
    case 'FAILED':
    case 'ERROR':
      return i18n.t('applicationForm.status.failed', { ns: 'jobForm' });
    default:
      return status || i18n.t('applicationForm.status.unknown', { ns: 'jobForm' });
  }
};

export const ApplicationStatusDisplay: React.FC<
  ApplicationStatusDisplayProps
> = ({
  data, loading = false }) => {
  const { t } = useTranslation('jobForm');
  const classes = useStyles();

  if (!data) {
    return null;
  }

  const { submission_status, error_message, api_response } = data;

  const hasError = error_message && error_message.trim() !== '';
  const isSubmitted = submission_status?.toUpperCase() === 'SUBMITTED';

  return (
    <Box className={classes.statusContainer} position="relative">
      {/* Subtle loading indicator at the top when refetching */}
      {/* {loading && (
        <LinearProgress
          className={classes.loadingProgressBar}
          color="info"
          variant="indeterminate"
        />
      )} */}

      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" component="h3">
          {t('applicationForm.status.title')}
        </Typography>
        {submission_status && (
          <Chip
            label={getStatusLabel(submission_status)}
            color={getStatusColor(submission_status)}
            variant={isSubmitted ? 'filled' : 'outlined'}
            className={classes.statusChip}
          />
        )}
      </Box>

      {isSubmitted && (
        <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
          <AlertTitle>{t('applicationForm.status.successTitle')}</AlertTitle>
          {t('applicationForm.status.successBody')}
        </Alert>
      )}

      {hasError && (
        <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
          <AlertTitle>{t('applicationForm.status.errorTitle')}</AlertTitle>
          {t('applicationForm.status.errorBody')}
          <Box className={classes.errorDetails}>
            <Typography variant="body2" component="div">
              <strong>{t('applicationForm.status.errorDetails')}</strong>
            </Typography>
            <pre>{error_message}</pre>
          </Box>
        </Alert>
      )}

      {api_response && (
        <Accordion
          defaultExpanded={false}
          elevation={0}
          sx={{
            mt: 2,
            boxShadow: 'none',
            backgroundColor: 'transparent',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="api-response-content"
            id="api-response-header"
          >
            <Typography variant="body2" component="div">
              <strong>{t('applicationForm.status.apiResponse')}</strong>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className={classes.apiResponse}>
              <pre>
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(api_response), null, 2);
                  } catch {
                    return api_response;
                  }
                })()}
              </pre>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default ApplicationStatusDisplay;
